/**
 * CommentsMarginColumn (#4/#1) — Google-Docs-style comment column.
 *
 * A fixed-width flex column that lives in the layout BETWEEN the editor and the
 * chat/properties rail, so the three share space via flexbox (no overlap, no
 * cramming). A sticky header holds the controls + the collapsed "Resueltos"
 * group; below it, each open thread renders as a card positioned vertically to
 * align with the paragraph it was made on (recomputed on scroll/resize).
 *
 * Reuses ALL of CommentsPanel's data logic (loadThreads / submitNewComment /
 * submitReply / toggleResolve) and the pure helpers in commentLogic.js.
 *
 * Mounted from EditorPage inside the main flex row:
 *   {commentsOpen && <CommentsMarginColumn editor={...} scrollContainer={...} … />}
 */

import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { X, MessageSquare, CornerDownRight, Check, RotateCcw, Send, Sparkles, Loader2, Copy, CheckCheck, Plus, Replace } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../lib/axios';
import { useConfigStore } from '../../store/configStore';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { OPEN_COMMENT_THREAD_EVENT } from '../../extensions/CommentExtension';
import { formatAnchorCitation, sortThreads, countReplies } from '../../lib/commentLogic';
import { safeNodeAt } from '../../lib/safeNodeAt';
import { TableMap } from '@tiptap/pm/tables';

const GAP = 8; // vertical gap between stacked cards

const escapeId = (s) => (window.CSS && window.CSS.escape ? window.CSS.escape(String(s)) : String(s));

const CommentsMarginColumn = ({
  editor, sheetId, open, onClose, scrollContainer, runLocked, onDocumentModified,
  getActiveSessionId, onProcessed, layoutSignal, onInsertText, onReplaceText,
}) => {
  const { apiKey, model } = useConfigStore();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false); // #6: IA procesando comentarios
  const [composing, setComposing] = useState(false);
  const [newBody, setNewBody] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [collapsedIds, setCollapsedIds] = useState(() => new Set()); // #6: tarjetas colapsadas
  const [showResolved, setShowResolved] = useState(false); // #6: grupo de resueltos
  const [copiedId, setCopiedId] = useState(null); // #4: copiar comentario al portapapeles

  const [positions, setPositions] = useState({}); // id -> { top, hasAnchor }
  const [layoutTick, setLayoutTick] = useState(0);

  const cardRefs = useRef({});
  const cardsAreaRef = useRef(null); // #1: origen de coordenadas para alinear tarjetas al párrafo
  const justOpenedRef = useRef(false); // #2: ignora el selectionUpdate del click que abre la tarjeta
  const anchoringRef = useRef(false); // guard: evita doble-submit/re-ancla por doble-click

  // #2/#4: hovering a card highlights the LINKED CONTENT and scrolls it into view.
  // For a text range mark that's the span itself; for a 💬 point marker (used by
  // image/table/point comments) the marker is tiny, so we ALSO highlight the block
  // it sits in and the element it annotates (the image/table just before it) —
  // otherwise a table/image comment gives no visible cue.
  const setAnchorHighlight = useCallback((id, on) => {
    if (!scrollContainer) return;
    const targets = new Set();
    // 1) Elements carrying data-comment-id (text/image/table anchors).
    const els = scrollContainer.querySelectorAll(`[data-comment-id="${escapeId(id)}"]`);
    els.forEach((el) => {
      targets.add(el);
      // A comment MARK wrapping an image is an inline <span> around an
      // inline-block image → its own box has no visible height, so the highlight
      // never shows. Illuminate the inner image/wrapper instead.
      const inner = el.querySelector && el.querySelector('.resizable-image-wrapper, img');
      if (inner) targets.add(inner);
      if (el.classList.contains('tiptap-comment-marker')) {
        const block = el.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote, td, th, table, figure, .resizable-image-wrapper');
        if (block) targets.add(block);
        if (el.previousElementSibling) targets.add(el.previousElementSibling);
      }
    });
    // 2) Reinforce via ProseMirror ONLY when the attribute selector found nothing
    //    (e.g. a mark span nested in a table cell's node view that the selector
    //    misses). Skipping the full doc scan when step 1 already has targets avoids
    //    an O(doc) walk on every hover. Highlights the TEXT span (never the cell).
    if (editor && els.length === 0) {
      editor.state.doc.descendants((node, pos) => {
        if (!node.isText) return;
        if (!node.marks.some((m) => m.type.name === 'comment' && String(m.attrs.commentId) === String(id))) return;
        let dom;
        try { dom = editor.view.domAtPos(pos + 1)?.node; } catch (e) { return; }
        const el = dom && (dom.nodeType === 3 ? dom.parentElement : dom);
        const span = el && el.closest && el.closest('.tiptap-comment');
        if (span || el) targets.add(span || el);
      });
    }
    targets.forEach((t) => t.classList && t.classList.toggle('comment-hover-highlight', on));
    // No scrollIntoView on hover: the card is aligned to its anchor, so the anchor
    // is already in view; scrollIntoView could bubble to the window (the earlier
    // "document jumps" bug). The highlight alone is enough to locate the text.
  }, [scrollContainer, editor]);

  // Scroll the document so the comment's anchor is centered — scoped to the
  // scrollContainer (never scrollIntoView, which bubbles to the window). No-op if
  // the anchor no longer exists in the doc (the paragraph/text was deleted).
  const centerAnchor = useCallback((id) => {
    const sc = scrollContainer;
    if (!sc) return;
    const anchor = sc.querySelector(`[data-comment-id="${escapeId(id)}"]`);
    if (!anchor) return;
    const aRect = anchor.getBoundingClientRect();
    const cRect = sc.getBoundingClientRect();
    const target = sc.scrollTop + (aRect.top - cRect.top) - (sc.clientHeight - aRect.height) / 2;
    sc.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [scrollContainer]);

  // Classify the CURRENT editor selection once and return { citation, apply }.
  // `citation` is the anchor quote for the panel/DB; `apply(commentId)` re-applies
  // the anchor (image/table attr, text mark, or 💬 marker) for that id. Shared by
  // creating a comment and re-anchoring an orphaned one (recover button).
  const classifyAnchor = useCallback(() => {
    if (!editor) return { citation: undefined, apply: () => {} };
    const selection = editor.state.selection;
    const { from, to } = selection;
    const selText = editor.state.doc.textBetween(from, to, ' ');
    const selNode = selection.node;
    const imagePos =
      selNode && (selNode.type.name === 'resizableImage' || selNode.type.name === 'image') ? from : null;

    let tablePos = null;
    let wholeTable = false;
    if (selection.$anchorCell) {
      const $cell = selection.$anchorCell;
      const tableNode = $cell.node(-1);
      tablePos = $cell.before(-1);
      const start = $cell.start(-1);
      const map = TableMap.get(tableNode);
      const rect = map.rectBetween(selection.$anchorCell.pos - start, selection.$headCell.pos - start);
      wholeTable = rect.left === 0 && rect.top === 0 && rect.right === map.width && rect.bottom === map.height;
    }

    const nodePos = imagePos != null ? imagePos : (wholeTable ? tablePos : null);
    const citation = imagePos != null
      ? '(imagen)'
      : selection.$anchorCell ? '(tabla)' : (selText.trim() || undefined);
    const ranges = selection.ranges.map((r) => ({ from: r.$from.pos, to: r.$to.pos }));
    const isCellOrText = Boolean(selection.$anchorCell) || Boolean(selText.trim());

    const apply = (commentId) => {
      if (!editor) return;
      if (nodePos != null) {
        // apply() corre tras un await: el doc pudo encogerse y nodePos quedar
        // fuera de rango — safeNodeAt evita el RangeError (T-080).
        const node = safeNodeAt(editor.state.doc, nodePos);
        const okType = imagePos != null
          ? node && (node.type.name === 'resizableImage' || node.type.name === 'image')
          : node && node.type.name === 'table';
        if (okType) {
          editor.view.dispatch(editor.state.tr.setNodeMarkup(nodePos, undefined, { ...node.attrs, commentId }));
          return;
        }
        const safePos = Math.min(to, editor.state.doc.content.size);
        editor.chain().focus().setTextSelection(safePos).insertCommentMarker(commentId).run();
        return;
      }
      if (isCellOrText) {
        const mark = editor.schema.marks.comment.create({ commentId });
        const size = editor.state.doc.content.size;
        let tr = editor.state.tr;
        // Bounds-check each captured range: the doc may have shrunk during the
        // await (autosave/remote edit). Out-of-range → skip (comment ends up
        // orphaned and recoverable) instead of throwing on addMark.
        ranges.forEach((r) => {
          if (r.to > r.from && r.to <= size) tr = tr.addMark(r.from, r.to, mark);
        });
        editor.view.dispatch(tr);
        return;
      }
      const safePos = Math.min(to, editor.state.doc.content.size);
      editor.chain().focus().setTextSelection(safePos).insertCommentMarker(commentId).run();
    };

    return { citation, apply };
  }, [editor]);

  const toggleCollapsed = useCallback((id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // #4: copiar el texto de un comentario al portapapeles (patrón ChatSidebar/MessageList).
  const copyComment = useCallback(async (id, text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch (e) {
      toast.error('No se pudo copiar');
    }
  }, []);

  // ---- Data logic (verbatim from CommentsPanel) ----------------------------
  const loadThreads = useCallback(async () => {
    if (!sheetId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/sheets/${sheetId}/comments`);
      setThreads(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar comentarios');
    } finally {
      setLoading(false);
    }
  }, [sheetId]);

  useEffect(() => {
    if (open) loadThreads();
  }, [open, loadThreads]);

  // #2: keep the doc's comment marks in sync with the backend status. Comments
  // resolved by the AI (#6) only PATCH the DB — the editor mark never learned it
  // was resolved and stayed yellow. Re-flag resolved marks here so #7's CSS
  // (resolved = no highlight) actually applies.
  // ponytail: only syncs resolved→true (the bug case); reopening already flips
  // the mark back via toggleResolve.
  useEffect(() => {
    if (!editor || !editor.commands?.setCommentResolved) return;
    threads.forEach((t) => {
      if (t.status === 'resolved') editor.commands.setCommentResolved(t.id, true);
    });
  }, [threads, editor]);

  const submitNewComment = async () => {
    if (!editor || !sheetId || !newBody.trim() || anchoringRef.current) return;
    anchoringRef.current = true; // guard against double-submit (duplicate POST/anchor)
    // Classify the selection ONCE: image/whole-table → node attribute; a partial
    // table selection or text range → mark; a bare cursor → 💬 marker.
    const { citation, apply } = classifyAnchor();
    try {
      const { data } = await axiosInstance.post(`/sheets/${sheetId}/comments`, {
        body: newBody.trim(),
        ...(citation ? { anchor_text: citation } : {}),
      });
      apply(data.id);
      setThreads((prev) => [data, ...prev]);
      setComposing(false);
      setNewBody('');
      setLayoutTick((t) => t + 1); // reposition against the freshly inserted anchor
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear comentario');
    } finally {
      anchoringRef.current = false;
    }
  };

  // Re-anchor an ORPHANED comment (its text/node was deleted) to the current
  // cursor/selection in the document, updating its citation. If text is selected
  // the mark wraps it; an image/table anchors on the node; a bare cursor drops a
  // 💬 marker.
  const recoverAnchor = async (thread) => {
    if (!editor || !sheetId || anchoringRef.current) return;
    anchoringRef.current = true; // guard against double re-anchor
    try {
      const { citation, apply } = classifyAnchor();
      apply(thread.id);
      setLayoutTick((t) => t + 1);
      setThreads((prev) => prev.map((t) => (t.id === thread.id ? { ...t, anchor_text: citation } : t)));
      try {
        await axiosInstance.patch(`/sheets/${sheetId}/comments/${thread.id}`, { anchor_text: citation ?? '' });
      } catch (e) {
        // The anchor is applied in the document regardless; the citation sync is
        // best-effort.
      }
      toast.success('Comentario re-anclado a la posición del cursor');
    } finally {
      anchoringRef.current = false;
    }
  };

  const submitReply = async (threadId) => {
    const body = (replyDrafts[threadId] || '').trim();
    if (!body || !sheetId) return;
    try {
      const { data } = await axiosInstance.post(`/sheets/${sheetId}/comments/${threadId}/replies`, { body });
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, replies: [...(t.replies || []), data] } : t))
      );
      setReplyDrafts((prev) => ({ ...prev, [threadId]: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al responder');
    }
  };

  const toggleResolve = async (thread) => {
    if (!sheetId) return;
    const nextStatus = thread.status === 'resolved' ? 'open' : 'resolved';
    try {
      const { data } = await axiosInstance.patch(`/sheets/${sheetId}/comments/${thread.id}`, { status: nextStatus });
      setThreads((prev) => prev.map((t) => (t.id === thread.id ? { ...t, ...data } : t)));
      if (editor?.commands?.setCommentResolved) {
        editor.commands.setCommentResolved(thread.id, nextStatus === 'resolved');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  // ---- #6: hand the sheet's OPEN comments to the AI to apply + resolve ------
  const processComments = async (commentIds) => {
    if (!sheetId || processing) return;
    setProcessing(true);
    try {
      const body = {
        api_key: apiKey || undefined,
        model: model || undefined,
        // #3: append the exchanges to the active chat (or let the server open one).
        session_id: (getActiveSessionId && getActiveSessionId()) || undefined,
        // #1: process only these comments if given; else all open ones.
        comment_ids: (Array.isArray(commentIds) && commentIds.length) ? commentIds : undefined,
      };
      const doPost = async (signal) => {
        const resp = await axiosInstance.post(
          `/sheets/${sheetId}/comments/process`, body, { signal, timeout: 300000 },
        );
        if (resp.data?.document_modified && onDocumentModified) {
          await onDocumentModified(resp.data.modified_sheet_ids || []);
        }
        return resp;
      };
      const resp = runLocked ? await runLocked(doPost) : await doPost();
      const n = resp.data?.processed ?? 0;
      toast.success(n ? `IA procesó ${n} comentario(s)` : 'No hay comentarios abiertos que procesar');
      // #3: surface the exchanges in the chat (opens/switches to the session).
      if (n && onProcessed && resp.data?.session_id) onProcessed(resp.data.session_id);
      await loadThreads();
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        toast.info('Operación cancelada');
      } else if (!error.__creditsHandled) {
        toast.error(error.response?.data?.detail || 'Error al procesar comentarios');
      }
    } finally {
      setProcessing(false);
    }
  };

  // ---- Click a mark in the doc → focus the matching card -------------------
  useEffect(() => {
    const handler = (event) => {
      const commentId = event.detail && event.detail.commentId;
      if (commentId === null || commentId === undefined) return;
      const id = String(commentId);
      justOpenedRef.current = true; // #2: skip the selectionUpdate from the opening click
      // Auto-clear next frame: a click that fires NO selectionUpdate (e.g. on a 💬
      // marker whose handleClick returns true) would otherwise leave the flag stuck
      // and swallow the user's next real click.
      requestAnimationFrame(() => { justOpenedRef.current = false; });
      setExpandedId(id);
      setHighlightId(id);
      // Center the anchor in the document (scoped; the card aligns to it). The
      // 90ms lets a just-opened column reflow before we measure.
      setTimeout(() => centerAnchor(id), 90);
      setTimeout(() => setHighlightId((current) => (current === id ? null : current)), 1500);
    };
    window.addEventListener(OPEN_COMMENT_THREAD_EVENT, handler);
    return () => window.removeEventListener(OPEN_COMMENT_THREAD_EVENT, handler);
  }, [centerAnchor]);

  // The floating "Comentar selección" button (above the AI button) opens this
  // column and asks it to start composing against the live selection.
  useEffect(() => {
    const onCompose = () => { setComposing(true); setNewBody(''); };
    window.addEventListener('startCommentCompose', onCompose);
    return () => window.removeEventListener('startCommentCompose', onCompose);
  }, []);

  // #2: clicking/selecting elsewhere in the doc dismisses the focused card. We
  // skip the selectionUpdate caused by the opening click itself (justOpenedRef).
  useEffect(() => {
    if (!editor) return undefined;
    const onSel = () => {
      if (justOpenedRef.current) { justOpenedRef.current = false; return; }
      if (!editor.isActive('comment')) {
        setExpandedId(null);
        setHighlightId(null);
      }
    };
    editor.on('selectionUpdate', onSel);
    return () => editor.off('selectionUpdate', onSel);
  }, [editor]);

  // ---- Recompute layout on editor update, scroll, and window resize --------
  useEffect(() => {
    if (!editor) return undefined;
    let raf = null;
    const bump = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; setLayoutTick((t) => t + 1); });
    };
    editor.on('update', bump);
    window.addEventListener('resize', bump);
    const sc = scrollContainer;
    if (sc) sc.addEventListener('scroll', bump, { passive: true });
    return () => {
      editor.off('update', bump);
      window.removeEventListener('resize', bump);
      if (sc) sc.removeEventListener('scroll', bump);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [editor, scrollContainer]);

  // ---- Two-pass positioning + Google-Docs overlap resolution ---------------
  // Cards align to the viewport position of their anchor, RELATIVE to the cards
  // area (which sits below the header) — so they never render under the header.
  // ponytail: cards whose paragraph is scrolled above the area clamp to the top;
  // those scrolled below are clipped (overflow-hidden) until scrolled into view.
  useLayoutEffect(() => {
    if (!open || !editor || !scrollContainer) return;
    const area = cardsAreaRef.current;
    if (!area) return;
    const areaRect = area.getBoundingClientRect();

    const measure = (id) => {
      const el = cardRefs.current[id];
      const h = el ? el.getBoundingClientRect().height : 0;
      return h || 96;
    };

    const openThreads = sortThreads(threads).filter((t) => t.status !== 'resolved');
    const anchored = [];
    const unanchored = [];
    openThreads.forEach((thread) => {
      const id = String(thread.id);
      const anchor = scrollContainer.querySelector(`[data-comment-id="${escapeId(id)}"]`);
      if (anchor) {
        const r = anchor.getBoundingClientRect();
        anchored.push({ id, top: r.top - areaRect.top }); // relative to the cards area
      } else {
        unanchored.push({ id });
      }
    });
    anchored.sort((a, b) => a.top - b.top);

    const next = {};
    let prevBottom = -Infinity;
    // ponytail: colisión resuelta con un empujón simple hacia abajo (sin reparto
    // óptimo ni animación) — techo aceptable.
    anchored.forEach(({ id, top }) => {
      let t = Math.max(top, 0); // never above the area (header sits above it)
      if (t < prevBottom + GAP) t = prevBottom + GAP;
      next[id] = { top: t, hasAnchor: true };
      prevBottom = t + measure(id);
    });
    unanchored.forEach(({ id }) => {
      const t = prevBottom === -Infinity ? 0 : prevBottom + GAP;
      next[id] = { top: t, hasAnchor: false };
      prevBottom = t + measure(id);
    });

    setPositions(next);
  }, [open, threads, expandedId, collapsedIds, layoutTick, layoutSignal, editor, scrollContainer]);

  if (!open) return null;

  const sorted = sortThreads(threads);
  const openThreads = sorted.filter((t) => t.status !== 'resolved');
  const resolvedThreads = sorted.filter((t) => t.status === 'resolved');
  const openCount = openThreads.length;

  // Comment ids still present in the doc (as a text mark or an image/table
  // attribute). A thread whose id isn't here is ORPHANED — its anchor text/node
  // was deleted. ponytail: one doc scan per render of the open column; cheap for
  // normal docs, no memo needed.
  const anchoredIds = new Set();
  if (editor) {
    editor.state.doc.descendants((node) => {
      node.marks.forEach((m) => {
        if (m.type.name === 'comment' && m.attrs.commentId != null) anchoredIds.add(String(m.attrs.commentId));
      });
      if (node.attrs && node.attrs.commentId != null) anchoredIds.add(String(node.attrs.commentId));
    });
  }

  const cards = openThreads.map((thread) => {
    const id = String(thread.id);
    const isExpanded = expandedId === id;
    const isCollapsed = collapsedIds.has(id); // #6
    const pos = positions[id];
    const positioned = !!pos;
    const orphaned = editor ? !anchoredIds.has(id) : false; // anchor text/node deleted
    // #1: the AI left a clarification note (couldn't resolve) → flag the card.
    const needsClarif = (thread.replies || []).some((r) => (r.body || '').startsWith('⚠️'));
    return (
      <div
        key={thread.id}
        ref={(el) => { cardRefs.current[id] = el; }}
        // #4: hovering the card illuminates its anchored paragraph.
        onMouseEnter={() => setAnchorHighlight(id, true)}
        onMouseLeave={() => setAnchorHighlight(id, false)}
        // Clicking the card scrolls the document to its paragraph (if it still
        // exists). Ignore clicks on the inner controls (buttons / reply box).
        onClick={(e) => {
          if (e.target.closest('button, textarea, input, a')) return;
          // No justOpenedRef here: a panel click fires no editor selectionUpdate,
          // so setting the flag would leave it stuck and eat the next real click.
          setHighlightId(id);
          centerAnchor(id);
        }}
        className={`absolute rounded-md border p-3 bg-white dark:bg-slate-900 shadow-sm transition-shadow border-amber-300 dark:border-amber-700 cursor-pointer ${
          pos && !pos.hasAnchor ? 'opacity-50' : ''
        } ${highlightId === id ? 'ring-2 ring-blue-400' : ''}`}
        style={{
          top: `${positioned ? pos.top : 0}px`,
          left: '8px',
          right: '8px',
          visibility: positioned ? 'visible' : 'hidden',
          zIndex: highlightId === id ? 5 : 1,
        }}
      >
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <p className="text-xs italic text-gray-500 dark:text-slate-400 truncate">
              {formatAnchorCitation(thread.anchor_text)}
            </p>
            {orphaned && (
              <p className="text-xs not-italic text-amber-600 dark:text-amber-400 mt-0.5">
                ⚠️ ancla eliminada
              </p>
            )}
            {!isCollapsed && <p className="text-sm mt-1 break-words">{thread.body}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {needsClarif && (
              <span title="La IA necesita aclaración" className="text-amber-500 text-sm leading-none">⚠️</span>
            )}
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              Abierto
            </span>
            {/* #6: colapsar / expandir la tarjeta */}
            <button
              type="button"
              title={isCollapsed ? 'Expandir' : 'Colapsar'}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xs px-1"
              onClick={() => toggleCollapsed(id)}
            >
              {isCollapsed ? '▸' : '▾'}
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <>
            {/* T-247: flex-wrap — la fila trae hasta 6 acciones y en el ancho
                angosto de la tarjeta desbordaba el cuadro (botón Copiar medio
                fuera). Envolver a una 2ª línea en vez de desbordar. */}
            <div className="flex justify-between items-center mt-2 flex-wrap gap-y-1">
              <button
                type="button"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                onClick={() => setExpandedId(isExpanded ? null : id)}
              >
                {countReplies(thread)} respuesta{countReplies(thread) === 1 ? '' : 's'} {isExpanded ? '▲' : '▼'}
              </button>
              <div className="flex items-center gap-1 flex-wrap justify-end min-w-0">
                {/* Re-anclar un comentario huérfano a la posición actual del cursor. */}
                {orphaned && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-amber-600 dark:text-amber-400"
                    title="Re-anclar este comentario donde está el cursor (o el texto seleccionado) en el documento"
                    onClick={() => recoverAnchor(thread)}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Recuperar
                  </Button>
                )}
                {/* #1: procesar SOLO este comentario con la IA. */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  disabled={processing}
                  title="Procesar este comentario con IA"
                  onClick={() => processComments([thread.id])}
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Procesar
                </Button>
                {/* #4: copiar el texto del comentario al portapapeles. */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  title="Copiar comentario"
                  onClick={() => copyComment(id, thread.body)}
                >
                  {copiedId === id ? <CheckCheck className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />} Copiar
                </Button>
                {/* #2: insertar / reemplazar el texto del comentario en el documento. */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  title="Insertar en el documento"
                  onClick={() => { onInsertText?.(thread.body); }}
                >
                  <Plus className="w-3 h-3 mr-1" /> Insertar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  title="Reemplazar la selección"
                  onClick={() => { onReplaceText?.(thread.body); }}
                >
                  <Replace className="w-3 h-3 mr-1" /> Reemplazar
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => toggleResolve(thread)}>
                  <Check className="w-3 h-3 mr-1" /> Resolver
                </Button>
              </div>
            </div>

            {isExpanded && (
              <div className="mt-2 pl-3 border-l-2 border-gray-200 dark:border-slate-700 space-y-2">
                {(thread.replies || []).map((r) => (
                  <div key={r.id} className="text-xs flex items-start gap-1">
                    <CornerDownRight className="w-3 h-3 mt-0.5 text-gray-400 shrink-0" />
                    <span className="break-words">{r.body}</span>
                  </div>
                ))}
                <div className="flex gap-1 items-start">
                  <Textarea
                    rows={1}
                    placeholder="Responder…"
                    className="text-xs min-h-[32px]"
                    value={replyDrafts[thread.id] || ''}
                    onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [thread.id]: e.target.value }))}
                  />
                  <Button size="sm" variant="ghost" onClick={() => submitReply(thread.id)}>
                    <Send className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  });

  return (
    <div className="h-full w-64 md:w-72 lg:w-80 flex-shrink-0 flex flex-col bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 slide-in">
      {/* Fixed header: controls + resolved group (never overlaps the cards). */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            Comentarios
          </span>
          <Button variant="ghost" size="sm" className="h-6 px-1" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {!composing ? (
          <div className="space-y-2">
            <Button size="sm" className="w-full" disabled={!editor} onClick={() => { setComposing(true); setNewBody(''); }}>
              Comentar selección
            </Button>
            {/* #6: la IA aplica los comentarios abiertos, responde y los resuelve. */}
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={processing || openCount === 0}
              onClick={() => processComments()}
            >
              {processing ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Procesando…</>
              ) : (
                <><Sparkles className="w-3 h-3 mr-1" /> Procesar todos{openCount ? ` (${openCount})` : ''}</>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Textarea
              autoFocus
              placeholder="Escribe tu comentario…"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={submitNewComment} disabled={!newBody.trim()}>Guardar</Button>
              <Button size="sm" variant="ghost" onClick={() => setComposing(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {loading && <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">Cargando…</p>}
        {!loading && sorted.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">Sin comentarios en esta hoja.</p>
        )}

        {/* #6: resolved comments collapse into a count-only group. */}
        {resolvedThreads.length > 0 && (
          <div className="mt-2 border-t border-gray-200 dark:border-slate-700 pt-2">
            <button
              type="button"
              className="w-full flex items-center justify-between text-xs text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100"
              onClick={() => setShowResolved((v) => !v)}
            >
              <span>Resueltos ({resolvedThreads.length})</span>
              <span>{showResolved ? '▲' : '▼'}</span>
            </button>
            {showResolved && (
              <div className="mt-2 space-y-2 max-h-56 overflow-y-auto">
                {resolvedThreads.map((thread) => (
                  <div
                    key={thread.id}
                    // #3: hovering a resolved comment illuminates its paragraph
                    // (resolved comments no longer open a panel on click, #2).
                    onMouseEnter={() => setAnchorHighlight(String(thread.id), true)}
                    onMouseLeave={() => setAnchorHighlight(String(thread.id), false)}
                    className="rounded border border-gray-200 dark:border-slate-700 p-2 opacity-80"
                  >
                    <p className="text-xs italic text-gray-500 dark:text-slate-400 truncate">
                      {formatAnchorCitation(thread.anchor_text)}
                    </p>
                    <p className="text-xs mt-0.5 break-words line-clamp-2">{thread.body}</p>
                    <div className="flex justify-between items-center mt-1">
                      {/* #4: historial de cambios (respuestas 🤖/📝) de este hilo. */}
                      {(thread.replies || []).length > 0 ? (
                        <button
                          type="button"
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          onClick={() => setExpandedId(expandedId === String(thread.id) ? null : String(thread.id))}
                        >
                          Historial ({thread.replies.length}) {expandedId === String(thread.id) ? '▲' : '▼'}
                        </button>
                      ) : <span />}
                      {/* #4: copiar el texto del comentario resuelto al portapapeles. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        title="Copiar comentario"
                        onClick={() => copyComment(String(thread.id), thread.body)}
                      >
                        {copiedId === String(thread.id) ? <CheckCheck className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />} Copiar
                      </Button>
                      {/* #2: insertar / reemplazar el texto del comentario resuelto en el documento. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        title="Insertar en el documento"
                        onClick={() => { onInsertText?.(thread.body); }}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Insertar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        title="Reemplazar la selección"
                        onClick={() => { onReplaceText?.(thread.body); }}
                      >
                        <Replace className="w-3 h-3 mr-1" /> Reemplazar
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => toggleResolve(thread)}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Reabrir
                      </Button>
                    </div>
                    {expandedId === String(thread.id) && (thread.replies || []).length > 0 && (
                      <div className="mt-1 pl-2 border-l-2 border-gray-200 dark:border-slate-700 space-y-1">
                        {thread.replies.map((r) => (
                          <p key={r.id} className="text-xs break-words">{r.body}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cards area: open threads positioned to align with their paragraphs. */}
      <div ref={cardsAreaRef} className="relative flex-1 overflow-hidden">
        {cards}
        {!loading && openThreads.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-slate-500 p-3">No hay comentarios abiertos.</p>
        )}
      </div>
    </div>
  );
};

export default CommentsMarginColumn;
